import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError } from "@/api/client";
import {
  addCustomerNote,
  customerKeys,
  editCustomerNote,
  getCustomerActivity,
  getCustomerCalls,
  getCustomerJobs,
  getCustomerNotes,
  getCustomerProfile,
  removeCustomerNote,
  revealCustomerPhone,
  type ActivityParams,
  type CustomerCallsParams,
  type CustomerJobsParams,
} from "@/api/customers";
import { describeError } from "@/lib/errors";

/**
 * Server state for the customer panel, the full customer page and the top-bar
 * search. A 403 or 404 is an answer (not your screen / no such customer),
 * never retried.
 */
const noRetryOnAnswer = (failureCount: number, error: unknown): boolean => {
  if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
    return false;
  }
  return failureCount < 2;
};

export function useCustomerProfile(id: number) {
  return useQuery({
    queryKey: customerKeys.profile(id),
    queryFn: ({ signal }) => getCustomerProfile(id, signal),
    enabled: id > 0,
    retry: noRetryOnAnswer,
    staleTime: 30_000,
  });
}

/** Newest page first; "Load earlier" fetches the page before the oldest shown. */
export function useCustomerActivity(id: number, params: ActivityParams = {}) {
  return useInfiniteQuery({
    queryKey: customerKeys.activity(id, params),
    queryFn: ({ pageParam, signal }) =>
      getCustomerActivity(id, params, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: id > 0,
    retry: noRetryOnAnswer,
    staleTime: 15_000,
  });
}

export function useCustomerNotes(id: number, include: "active" | "all" = "active") {
  return useQuery({
    queryKey: [...customerKeys.notes(id), include],
    queryFn: ({ signal }) => getCustomerNotes(id, include, signal),
    enabled: id > 0,
    retry: noRetryOnAnswer,
    staleTime: 30_000,
  });
}

export function useCustomerJobs(id: number, params: CustomerJobsParams) {
  return useQuery({
    queryKey: customerKeys.jobs(id, params),
    queryFn: ({ signal }) => getCustomerJobs(id, params, signal),
    enabled: id > 0,
    retry: noRetryOnAnswer,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useCustomerCalls(
  id: number,
  params: CustomerCallsParams,
  enabled = true,
) {
  return useQuery({
    queryKey: customerKeys.calls(id, params),
    queryFn: ({ signal }) => getCustomerCalls(id, params, signal),
    enabled: enabled && id > 0,
    retry: noRetryOnAnswer,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/** The server's words for a refusal are written for people; use them. */
export function describeCustomerError(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) {
    return "That customer or note no longer exists.";
  }
  return describeError(error);
}

/** After any change to a note: the profile's latest three, the notes list and the timeline. */
function useRefreshAfterNote(customerId: number) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: customerKeys.profile(customerId) }),
      queryClient.invalidateQueries({ queryKey: customerKeys.notes(customerId) }),
      queryClient.invalidateQueries({ queryKey: customerKeys.activities(customerId) }),
    ]);
}

export function useAddNote(customerId: number) {
  const refresh = useRefreshAfterNote(customerId);
  return useMutation({
    mutationFn: (input: { text: string; ticketId?: number }) =>
      addCustomerNote(customerId, input),
    onSuccess: () => toast.success("Note saved"),
    onError: (error) => toast.error(describeCustomerError(error)),
    onSettled: refresh,
  });
}

export function useEditNote(customerId: number) {
  const refresh = useRefreshAfterNote(customerId);
  return useMutation({
    mutationFn: ({ noteId, text }: { noteId: number; text: string }) =>
      editCustomerNote(customerId, noteId, text),
    onSuccess: () => toast.success("Note updated"),
    onError: (error) => toast.error(describeCustomerError(error)),
    onSettled: refresh,
  });
}

export function useRemoveNote(customerId: number) {
  const refresh = useRefreshAfterNote(customerId);
  return useMutation({
    mutationFn: (noteId: number) => removeCustomerNote(customerId, noteId),
    onSuccess: () => toast.success("Note removed"),
    onError: (error) => toast.error(describeCustomerError(error)),
    onSettled: refresh,
  });
}

/**
 * The whole numbers. A mutation, not a query: the result is held by the panel
 * that asked and never enters the query cache.
 */
export function useRevealPhone(customerId: number) {
  return useMutation({
    mutationFn: () => revealCustomerPhone(customerId),
    onError: (error) => toast.error(describeCustomerError(error)),
  });
}
